import type { Category } from '../api/client'

// Colour represents category on every screen, matching the API. The values
// are the category tokens (ui/tokens.css), the same hexes the API sends.
export const CATEGORIES: { value: Category; label: string; colour: string }[] = [
  { value: 'drainage', label: 'Drainage', colour: 'var(--cat-drainage)' },
  { value: 'relax', label: 'Relaxation', colour: 'var(--cat-relax)' },
  { value: 'consult', label: 'Consultation', colour: 'var(--cat-consult)' },
]

// A booking whose treatment has no colour of its own.
export const NO_CATEGORY_COLOUR = 'var(--cat-consult)'

// Index 0 is Monday, as in the API.
export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

// priceInput shows pence the way someone would type it: "119" or "80.50".
export function priceInput(pence: number): string {
  const pounds = Math.floor(pence / 100)
  const rest = pence % 100
  return rest === 0 ? String(pounds) : `${pounds}.${String(rest).padStart(2, '0')}`
}

// formatPrice is the one way both products write money (docs/25): "£80",
// "£80.50", "£1,250" — real pence, no ".00" on whole pounds, a thousands
// comma, and a minus sign (never red) for a negative amount: "−£120".
const WHOLE_POUNDS = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 })
const WITH_PENCE = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function formatPrice(pence: number): string {
  const abs = Math.abs(Math.round(pence))
  const text = abs % 100 === 0 ? WHOLE_POUNDS.format(abs / 100) : WITH_PENCE.format(abs / 100)
  return pence < 0 && abs !== 0 ? `−${text}` : text
}

// priceOrFree is a price as people read it on a menu, a treatment or an
// appointment: nothing to pay is "Free", never "£0" (review 2026-10-14).
// Totals and amounts owed still use formatPrice.
export function priceOrFree(pence: number): string {
  return pence > 0 ? formatPrice(pence) : 'Free'
}

// possessive is "Glow Studio's", "Tara Quinn Therapies’": the same rule as
// the server's email.Possessive (never "Therapies’s").
export function possessive(name: string): string {
  const trimmed = name.trim()
  return /s$/i.test(trimmed) ? `${trimmed}’` : `${trimmed}’s`
}

// parsePrice accepts "99", "99.5" or "£99.50" and returns pence, or null.
export function parsePrice(input: string): number | null {
  const match = /^(\d{1,4})(?:\.(\d{1,2}))?$/.exec(input.trim().replace(/^£\s*/, ''))
  if (!match) {
    return null
  }
  return Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'))
}

// parseWholeNumber reads a whole-number field: digits only (spaces around are
// fine). Anything else — "1e6", "12.5", "2,4", "-3" — is null, so the form can
// say so instead of quietly saving 1, 12 or 2 (review 2026-10-14).
export function parseWholeNumber(input: string): number | null {
  const text = input.trim()
  if (!/^\d{1,9}$/.test(text)) {
    return null
  }
  return Number(text)
}

// noticeSpan says her cancellation notice the one way every surface does
// (the app, the booking site and emails: email.NoticeSpan in the API, QA
// 2026-10-14): in hours under two days or when not whole days ("24 hours",
// "36 hours", "1 hour"), in days from two whole days ("2 days", "7 days").
export function noticeSpan(hours: number): string {
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`
  return `${hours} hour${hours === 1 ? '' : 's'}`
}

// A treatment's length, always in minutes ("60 min", "90 min"), as a spa menu
// reads; formatDuration is for spans of time in the diary ("2 h").
export function formatLength(minutes: number): string {
  return `${minutes} min`
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) {
    return `${rest} min`
  }
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

// timeToMinutes reads an "HH:MM" value from a time input; null when empty or invalid.
export function timeToMinutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value)
  if (!match) {
    return null
  }
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (minutes > 59 || hours > 24 || (hours === 24 && minutes !== 0)) {
    return null
  }
  return hours * 60 + minutes
}

// Month names are written here rather than by Intl: browsers disagree on
// short September ("Sep" in Safari, "Sept" in newer Chrome), and one product
// should write one date one way (design review P11).
export const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// formatDay shows a calendar date ("2026-12-24" as "24 Dec 2026") without
// shifting it by time zone.
export function formatDay(isoDate: string): string {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number)
  return `${d} ${SHORT_MONTHS[m - 1]} ${y}`
}

export function formatDateRange(startsOn: string, endsOn: string): string {
  return startsOn === endsOn ? formatDay(startsOn) : `${formatDay(startsOn)} – ${formatDay(endsOn)}`
}
